/**
 * canonical-internal-path — the ONE spelling policy for an internal URL.
 *
 * THE BUG CLASS THIS CLOSES. Two different things decide what an internal URL
 * "should" be, and until this module they were two different implementations:
 *
 *   1. the REQUEST side — `server/middleware/redirects.ts` 301s a non-canonical
 *      spelling (`/zh-HK/blog`, `/about/`, `/free-tools/hk-school-map`) to its
 *      canonical twin;
 *   2. the LINK side — nothing. Code-generated links cannot express a stale
 *      spelling (the `routes.*` builders are pinned to `manifests/routes.yaml`),
 *      but DB-resident blog-post BODIES are author-written HTML and can express
 *      anything.
 *
 * The 2026-08-08 Ahrefs crawl priced that asymmetry: three posts that RECEIVE
 * organic traffic sat behind a 301 purely because other posts' bodies linked to
 * `/zh-HK/blog/<slug>` — zh-HK is the DEFAULT locale and is served UNPREFIXED,
 * so every one of those anchors burns a redirect hop and splits link equity.
 * The same bodies carry `/flat-rentals/hong-kong/search`, `/free-tools/…`,
 * `/how-to-list` and `/partners/`, each a live entry in the redirect map.
 *
 * The fix is not a bigger redirect table — it is making the two sides ONE
 * function. `canonicalInternalUrl` is the policy; the middleware asks "is the
 * requested URL its own canonical spelling?" and 301s when it is not, while the
 * post-body transform asks the same question of every anchor and rewrites when
 * it is not. A link the middleware would redirect is therefore unrepresentable
 * in a rendered body, and the two can never drift, because there is only one of
 * them.
 *
 * Pure and framework-free (no hono, no React, no I/O) precisely so BOTH callers
 * can hold it: the server middleware and a component that also renders in the
 * browser.
 */
import { buildLocalePath, localeFromPath, stripLocale } from '@letsgethome/lib/weburl';
import { routes, type Route } from '@letsgethome/routes';
import { categorySlugForAnyName } from '@letsgethome/types/blog-categories';
import { DEFAULT_LOCALE } from '@letsgethome/types/branded';

// Targets are TYPED route-builder results, never string literals (SSOT-W6):
// the `routes.*` builders are pinned to `manifests/routes.yaml` by the
// routes-package parity gate, so a renamed/retired route breaks THIS map at
// compile time instead of silently 301ing visitors into a 404. (The KEYS are
// retired Rails vocabulary — they must NOT resolve to live routes.)
const LEGACY_PATH_REDIRECTS: Record<string, Route> = {
  '/terms-of-service': routes.terms(),
  '/privacy-policy': routes.privacy(),
  '/free-tools/stamp-duty-calculator-rent': routes.tools.stampDuty(),
  '/free-tools/rental-yield-calculator': routes.tools.rentalYield(),
  '/free-tools/rates-government-rent-calculator': routes.tools.ratesGovernmentRent(),
  '/free-tools/buy-vs-rent-calculator': routes.tools.buyVsRent(),
  '/free-tools/chinese-converter-for-real-estate': routes.tools.chineseConverter(),
  '/free-tools/hk-school-map': routes.tools.schoolMap(),
  '/listings/new': routes.home(),
  '/flat-rentals/hong-kong/search': routes.listings.rent(),
  // Retired owner-facing Rails page. It 404'd in every locale while still
  // carrying inbound internal links (Ahrefs 2026-07-20, `/how-to-list` +
  // `/zh-CN/how-to-list`). `/services` is the live owner-facing equivalent —
  // the nearest page that answers "how do I list my property with you".
  '/how-to-list': routes.services(),
};

// Rails-era article URLs (`/blog/<slug>/<id>`, `/news/<slug>[/<id>]`) are
// deliberately NOT here: the blind 301 rewrote `/blog/<slug>` with the slug
// VERBATIM and no existence check — legacy Rails slugs are the post TITLES
// verbatim, while current slugs are sanitized, so most zh targets 404'd (the
// Ahrefs "Broken redirect" class). They are REAL routes
// (marketing/{blog-legacy-post,news-legacy-post,news-legacy}) whose shared
// loader resolves the CURRENT slug through the DB-backed legacy-slug lane
// (GetPost → 412 envelope → 301) and answers 410 Gone for dead URLs (PR-A2).
// The id-stripping half of that class is handled on the link side by
// `canonicalizeArticleLinks`, which runs BEFORE this policy on every anchor.

// The DEFAULT locale is never URL-prefixed (root-collapse policy: zh-HK lives
// at `/`, not `/zh-HK`). Derived from the generated DEFAULT_LOCALE, so a
// default-locale change moves this rule automatically (R4 PR-1).
const DEFAULT_LOCALE_PREFIX_RE = new RegExp(`^/${DEFAULT_LOCALE}(?=/|$)`);

/**
 * The canonical SPELLING of a path: no repeated slashes, no trailing slash
 * (the root `/` excepted).
 *
 * React Router matches `/about/` and `/about` alike, so EVERY page had a live
 * `/path/` twin serving a 200 — a crawlable duplicate whose only defense was
 * the canonical tag. That is the same duplicate-200 class the `/zh-HK/*` strip
 * closes for locale prefixes; rel-canonical is a hint, a 301 is not.
 *
 * Applied FIRST, so a non-canonical legacy URL (`/terms-of-service/`) resolves
 * to its final target in ONE hop rather than chaining.
 */
function canonicalSpelling(pathname: string): string {
  const collapsed = pathname.replace(/\/{2,}/g, '/');
  if (collapsed.length <= 1) return collapsed;
  return collapsed.replace(/\/+$/, '') || '/';
}

/**
 * The canonical `pathname + search` for an internal URL, or the input
 * recomposed unchanged when it is already canonical.
 *
 * Callers compare the result against what they hold: the middleware 301s on a
 * difference, the body transform rewrites the anchor on a difference. Lane
 * order is load-bearing and matches the order a request used to traverse the
 * middleware, so a URL that several lanes could claim still lands on its final
 * target in one step.
 *
 * @param pathname - Path only, leading `/`, never a full URL
 * @param search - Query string INCLUDING the leading `?`, or `''`
 */
export function canonicalInternalUrl(pathname: string, search = ''): string {
  const path = canonicalSpelling(pathname);
  const pathWithoutLocale = stripLocale(path);
  const locale = localeFromPath(path);

  // Static redirect map. Rebuilt under the visitor's OWN locale
  // (`buildLocalePath` re-adds the prefix, or none for the default), so the
  // target preserves the language rather than dumping everyone on zh-HK.
  const mapped = LEGACY_PATH_REDIRECTS[pathWithoutLocale];
  if (mapped) return buildLocalePath(locale, mapped) + search;

  // Dynamic: /listings/new/:type → /
  if (pathWithoutLocale.startsWith('/listings/new/')) {
    return buildLocalePath(locale, '/') + search;
  }

  // Legacy `/blog?category=<name>` filter URLs → the canonical, indexable
  // `/blog/category/{slug}` page (SEO A3, ADR 0152). The old filter matched a
  // category by its display name in ANY locale column, so resolve all three
  // via the generated `categorySlugForAnyName`; a name outside the closed
  // vocabulary falls through (the plain index still renders — never a dead
  // redirect). `page` is carried, `q` is dropped: the canonical category page
  // has no free-text search, and a `?category=&q=` URL is a vanishingly-rare
  // noindex variant. Placed BEFORE the default-locale strip so a
  // `/zh-HK/blog?category=` collapses to its canonical in ONE hop.
  if (pathWithoutLocale === '/blog' && search !== '') {
    const params = new URLSearchParams(search);
    const categoryName = params.get('category');
    const slug = categoryName ? categorySlugForAnyName(categoryName) : undefined;
    if (slug) {
      const page = Number.parseInt(params.get('page') ?? '', 10);
      const opts = page > 1 ? { page } : undefined;
      return buildLocalePath(locale, routes.blog.category(slug, opts));
    }
  }

  // `/zh-HK/*` duplicated EVERY unprefixed page as a crawlable 200 (only the
  // canonical tag pointed home) — collapse to the stripped path, query
  // preserved. LAST among the rewriting lanes: the lanes above already rebuild
  // their target under the visitor's locale (unprefixed for the default), so a
  // zh-HK-prefixed legacy URL reaches its canonical in ONE step; this catches
  // the remaining plain `/zh-HK/<live page>` duplicates.
  if (DEFAULT_LOCALE_PREFIX_RE.test(path)) {
    return (path.replace(DEFAULT_LOCALE_PREFIX_RE, '') || '/') + search;
  }

  // No lane claimed it. `path` is already the canonical spelling, so this
  // returns the input unchanged for a canonical URL and the normalized
  // spelling for `/about/` or `//about`.
  return path + search;
}
